import { computed, readonly, ref, useContext } from '@nuxtjs/composition-api';

import type { ComposableFunctionArgs, Countries } from '~/composables/types';
import { Logger } from '~/helpers/logger';
import { publicReadKey, publicReadScope, readPublicMetadata } from '~/helpers/publicReadCache';
import type { Country, Maybe } from '~/modules/GraphQL/types';

import type { UseCountrySearchInterface } from './useCountrySearch';

/**
 * Allows fetching a list of countries or a single country by ID
 *
 * See the {@link UseCountrySearchInterface} for a list of methods and values available in this composable.
 */
export function useCountrySearch(): UseCountrySearchInterface {
  const context = useContext();
  const { app } = context;
  const pending = ref(0);
  const loading = computed(() => pending.value > 0);
  let searchSequence = 0;
  let loadSequence = 0;
  const error = ref({
    search: null,
    load: null,
  });

  const search = async (params: ComposableFunctionArgs<{ id: string }>): Promise<Maybe<Country>> => {
    let results = null;
    const sequence = ++searchSequence;

    try {
      pending.value += 1;
      error.value.search = null;

      Logger.debug('[Magento]: Search country information based on', { params });
      const key = publicReadKey('country', publicReadScope(app, context.$config?.currentSite), params);
      results = await readPublicMetadata(app, key, async () => {
        const { data, errors } = await app.$vsf.$magento.api.country(params.id, params?.customQuery ?? null, params?.customHeaders ?? null);
        if (errors?.length || !data?.country) throw new Error('Unable to load country information');
        return data.country;
      });
    } catch (err) {
      if (sequence === searchSequence) error.value.search = err;
      Logger.error('useCountrySearch/search', err);
    } finally {
      pending.value -= 1;
    }

    return results;
  };

  const load = async (): Promise<Array<Countries>> => {
    let results = [];
    const sequence = ++loadSequence;

    try {
      pending.value += 1;
      error.value.load = null;

      Logger.debug('[Magento]: Load available countries on store');
      const key = publicReadKey('countries', publicReadScope(app, context.$config?.currentSite));
      results = await readPublicMetadata(app, key, async () => {
        const { data, errors } = await app.$vsf.$magento.api.countries();
        if (errors?.length || !Array.isArray(data?.countries)) throw new Error('Unable to load available countries');
        return data.countries;
      });
    } catch (err) {
      if (sequence === loadSequence) error.value.load = err;
      Logger.error('useCountrySearch/load', err);
    } finally {
      pending.value -= 1;
    }

    return results;
  };

  return {
    load,
    search,
    loading: readonly(loading),
    error: readonly(error),
  };
}

export * from './useCountrySearch';
export default useCountrySearch;
